/**
 * WebSocketServerTransport — `ws`-based WebSocket server.
 *
 * Accepts one client at a time. Implements ServerTransport from @skaile/workspaces/types.
 * Built on the `ws` package + `node:http`, both of which run under Node and Bun.
 */

import { createServer, type Server as HttpServer } from "node:http";
import type { AgentCommand, AgentEvent, ServerTransport } from "@skaile/workspaces/types";
import { type WebSocket, WebSocketServer } from "ws";
import { decodeBearerFromHeader, tokensEqual } from "./auth.js";

/**
 * Options for {@link WebSocketServerTransport}.
 *
 * @param port - TCP port to bind to.
 * @param onLog - Optional callback for internal log lines (client connect/disconnect events).
 * @param authToken - When set, every upgrade must present this token via the
 *   `skaile-bearer.<base64url>` subprotocol or the handshake is rejected with
 *   HTTP 401. When unset, no auth is enforced (back-compatible default).
 *
 * @docLink packages/transport/dev-guide#websocket-server-transport
 */
export interface WebSocketServerOptions {
  port: number;
  onLog?: (line: string) => void;
  authToken?: string;
}

/**
 * Shape-validate a `session_init` frame at ingress before forwarding to the
 * runner. Protocol v3 requires the deterministic bootstrap envelope to carry
 * an identity tuple, a semver-shaped `protocolVersion`, a resolved config
 * object, and a `credentials` map keyed by `mounts` / `connectors`. Frames
 * that fail the guard are rejected on the wire so the runner never sees a
 * malformed boot envelope.
 *
 * Returns `null` when the frame is well-formed, or a human-readable reason
 * when it should be rejected.
 *
 * Spec: `_devlog/specs/2026-05-10-deterministic-session-bootstrap.md`
 * § "SessionInitCommand schema".
 *
 * @internal
 */
function validateSessionInitShape(cmd: Record<string, unknown>): string | null {
  if (cmd.type !== "session_init") return "type must be 'session_init'";

  const identity = cmd.identity as Record<string, unknown> | undefined;
  if (!identity || typeof identity !== "object" || Array.isArray(identity)) {
    return "identity must be an object";
  }
  if (typeof identity.containerId !== "string") return "identity.containerId must be a string";
  if (typeof identity.sessionId !== "string") return "identity.sessionId must be a string";
  if (typeof identity.projectId !== "string") return "identity.projectId must be a string";

  const protocolVersion = cmd.protocolVersion as Record<string, unknown> | undefined;
  if (!protocolVersion || typeof protocolVersion !== "object" || Array.isArray(protocolVersion)) {
    return "protocolVersion must be an object";
  }
  if (typeof protocolVersion.major !== "number") return "protocolVersion.major must be a number";
  if (typeof protocolVersion.minor !== "number") return "protocolVersion.minor must be a number";
  if (typeof protocolVersion.patch !== "number") return "protocolVersion.patch must be a number";

  if (
    !cmd.resolvedConfig ||
    typeof cmd.resolvedConfig !== "object" ||
    Array.isArray(cmd.resolvedConfig)
  ) {
    return "resolvedConfig must be an object";
  }

  if (!cmd.credentials || typeof cmd.credentials !== "object" || Array.isArray(cmd.credentials)) {
    return "credentials must be an object";
  }

  return null;
}

/**
 * `ServerTransport` implementation backed by the `ws` package on top of a
 * `node:http` server.
 *
 * Enforces a single-client policy: if a new connection arrives while a client is
 * already connected, the stale connection is closed with code 4001 and replaced.
 * Incoming frames are parsed as JSON `AgentCommand` messages and fanned out to
 * registered `onCommand` handlers; outgoing `AgentEvent` objects are JSON-serialised
 * before sending. Call `listen()` to start accepting connections and `close()` to
 * shut down cleanly — `close()` terminates the active socket immediately rather
 * than keeping the event loop alive.
 *
 * @example
 * ```ts
 * const server = new WebSocketServerTransport({ port: 8080 });
 * await server.listen();
 * server.onCommand((cmd) => console.log(cmd));
 * server.send({ type: 'agent_end' });
 * await server.close();
 * ```
 *
 * @docLink packages/transport/dev-guide#websocket-server-transport
 */
export class WebSocketServerTransport implements ServerTransport {
  private httpServer: HttpServer | null = null;
  private wss: WebSocketServer | null = null;
  private activeClient: WebSocket | null = null;
  private readonly port: number;
  private readonly log: (line: string) => void;
  private readonly authToken?: string;

  private readonly commandHandlers = new Set<(command: AgentCommand) => void>();
  private readonly connectHandlers = new Set<() => void>();
  private readonly disconnectHandlers = new Set<() => void>();

  constructor(options: WebSocketServerOptions) {
    this.port = options.port;
    this.log = options.onLog ?? console.log;
    this.authToken = options.authToken;
  }

  /** Start the server and accept connections. */
  async listen(): Promise<void> {
    const httpServer = createServer((_req, res) => {
      res.writeHead(200, { "content-type": "text/plain" });
      res.end("skaile agent server");
    });
    // When an auth token is configured, reject the upgrade at handshake time
    // (HTTP 401) unless the client presents the matching bearer subprotocol.
    // Rejecting here — before `connection` — keeps unauthenticated sockets from
    // ever reaching the command handlers.
    const expected = this.authToken;
    const wss = new WebSocketServer({
      server: httpServer,
      verifyClient: expected
        ? (info, cb) => {
            const presented = decodeBearerFromHeader(info.req.headers["sec-websocket-protocol"]);
            if (presented !== null && tokensEqual(presented, expected)) {
              cb(true);
            } else {
              this.log("[ws-server] rejecting unauthenticated upgrade");
              cb(false, 401, "Unauthorized");
            }
          }
        : undefined,
    });
    wss.on("connection", (ws) => this.handleConnection(ws));
    this.httpServer = httpServer;
    this.wss = wss;

    await new Promise<void>((resolve, reject) => {
      httpServer.once("error", reject);
      httpServer.listen(this.port, () => {
        httpServer.off("error", reject);
        resolve();
      });
    });

    const addr = httpServer.address();
    const boundPort = typeof addr === "object" && addr ? addr.port : this.port;
    this.log(`[ws-server] listening on port ${boundPort}`);
  }

  private handleConnection(ws: WebSocket): void {
    if (this.activeClient) {
      // Close the stale connection instead of rejecting the new one.
      // This handles the race where waitForWsReady's probe hasn't
      // fully disconnected before the real gateway client connects.
      this.log("[ws-server] replacing previous connection");
      try {
        this.activeClient.close(4001, "Replaced by new connection");
      } catch {
        // already closed
      }
    }
    this.log("[ws-server] client connected");
    this.activeClient = ws;
    for (const handler of this.connectHandlers) {
      handler();
    }

    ws.on("message", (data) => {
      try {
        const parsed = JSON.parse(String(data)) as Record<string, unknown>;
        // Protocol v3: shape-validate `session_init` at ingress so the
        // runner never receives a malformed boot envelope. Other command
        // shapes pass through unchanged — invalid `AgentCommand`s surface
        // through the existing per-command handlers in serve.ts.
        if (parsed && typeof parsed === "object" && parsed.type === "session_init") {
          const reason = validateSessionInitShape(parsed);
          if (reason !== null) {
            this.log(`[ws-server] rejecting invalid session_init: ${reason}`);
            this.sendRaw({
              type: "error",
              message: `Invalid session_init: ${reason}`,
              fatal: false,
            });
            return;
          }
        }
        const cmd = parsed as unknown as AgentCommand;
        for (const handler of this.commandHandlers) {
          handler(cmd);
        }
      } catch (err) {
        this.sendRaw({
          type: "error",
          message: `Invalid message: ${err instanceof Error ? err.message : String(err)}`,
          fatal: false,
        });
      }
    });

    ws.on("close", () => {
      this.log("[ws-server] client disconnected");
      if (this.activeClient === ws) {
        this.activeClient = null;
        for (const handler of this.disconnectHandlers) {
          handler();
        }
      }
    });
  }

  /**
   * Stop the server.
   *
   * Terminates the active socket immediately — otherwise a still-connected
   * client would keep the event loop alive and block process shutdown.
   */
  async close(): Promise<void> {
    try {
      this.activeClient?.terminate();
    } catch {
      // already closed
    }
    this.activeClient = null;
    this.wss?.close();
    this.wss = null;
    const httpServer = this.httpServer;
    this.httpServer = null;
    if (httpServer) {
      httpServer.closeAllConnections();
      await new Promise<void>((resolve) => httpServer.close(() => resolve()));
    }
  }

  send(event: AgentEvent): void {
    this.sendRaw(event);
  }

  onCommand(handler: (command: AgentCommand) => void): () => void {
    this.commandHandlers.add(handler);
    return () => {
      this.commandHandlers.delete(handler);
    };
  }

  onConnect(handler: () => void): () => void {
    this.connectHandlers.add(handler);
    return () => {
      this.connectHandlers.delete(handler);
    };
  }

  onDisconnect(handler: () => void): () => void {
    this.disconnectHandlers.add(handler);
    return () => {
      this.disconnectHandlers.delete(handler);
    };
  }

  get connected(): boolean {
    return this.activeClient !== null;
  }

  private sendRaw(data: unknown): void {
    try {
      const client = this.activeClient;
      if (client && client.readyState === 1) {
        client.send(JSON.stringify(data));
      }
    } catch {
      // Silently ignore send failures
    }
  }
}
